import { useState, useEffect, useRef } from 'react';
import api, { API_BASE } from '../api';
import { useToast } from './ToastContext';
import PhotoEditor from './PhotoEditor';
import { T } from '../styles/theme';

const AI_TEXTS = ['✍️ Analizzo il prodotto...', '🔍 Cerco le parole chiave migliori...', '📝 Scrivo il titolo ottimizzato...', '💰 Calcolo il prezzo ideale...', '🏷️ Genero gli hashtag perfetti...', '✅ Quasi pronto...'];

export default function AnnuncioAIModal({ product, onClose }) {
  const toast = useToast();
  const [step, setStep] = useState(0); // 0=piattaforma, 1=genera, 2=modifica, 3=pubblica
  const [piattaforma, setPiattaforma] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [aiTextIdx, setAiTextIdx] = useState(0);
  const [result, setResult] = useState(null);
  const [edit, setEdit] = useState({ titolo: '', descrizione: '', prezzo: 0, tags: '' });
  const [publishing, setPublishing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ebayStatus, setEbayStatus] = useState(null);
  const [isMobile] = useState(window.innerWidth < 768);
  const [fotoModificate, setFotoModificate] = useState([]);
  const [waitingVintedConfirm, setWaitingVintedConfirm] = useState(false);
  const [vintedPublishData, setVintedPublishData] = useState(null);
  const [confirmingVinted, setConfirmingVinted] = useState(false);
  const [showEbaySuccess, setShowEbaySuccess] = useState(null);
  const [showEditorFoto, setShowEditorFoto] = useState(false);
  const [editorMontato, setEditorMontato] = useState(false); // monta 1 volta, poi mai smonta
  const [salvandoFoto, setSalvandoFoto] = useState(false);
  const [fotoSalvateCount, setFotoSalvateCount] = useState(0);
  const editorRef = useRef(null);

  useEffect(() => {
    api.get('/auth/ebay-status').then(r => setEbayStatus(r.data)).catch(() => {});
  }, []);

  // Listener: quando l'utente torna dalla tab Vinted
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && waitingVintedConfirm) {
        // L'utente è tornato — il popup è già visibile
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [waitingVintedConfirm]);

  useEffect(() => {
    if (!generating) return;
    const iv = setInterval(() => setAiTextIdx(p => (p + 1) % AI_TEXTS.length), 2500);
    return () => clearInterval(iv);
  }, [generating]);

  const fotoUrls = product
    ? ((product.photos || []).map(p => p.foto_url).length > 0
      ? (product.photos || []).map(p => p.foto_url)
      : (product.foto_url ? [product.foto_url] : []))
    : [];

  const handleSelectPiattaforma = async (p) => {
    setPiattaforma(p);
    setStep(1);
    setGenerating(true);
    setAiTextIdx(0);
    try {
      const res = await api.post('/annunci/genera', { product_id: product.id, piattaforma: p });
      setResult(res.data);
      const isVinted = p === 'vinted';
      setEdit({
        titolo: isVinted ? (res.data.titolo_vinted || '') : (res.data.titolo_ebay || ''),
        descrizione: isVinted ? (res.data.descrizione_vinted || '') : (res.data.descrizione_ebay || ''),
        prezzo: res.data.prezzo_suggerito || product.price || 0,
        tags: isVinted ? (res.data.tags_vinted || []).join(', ') : '',
      });
      setStep(2);
      toast.success('Annuncio generato!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore generazione AI');
      setStep(0);
    } finally { setGenerating(false); }
  };

  const handleRigenera = () => handleSelectPiattaforma(piattaforma);

  const handleSaveBozza = async () => {
    setSaving(true);
    try {
      await api.post('/bozze', {
        titolo: edit.titolo,
        descrizione: edit.descrizione,
        prezzo: parseFloat(edit.prezzo) || 0,
        categoria: product.category || '',
        hashtag: edit.tags ? edit.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        piattaforma: piattaforma,
        foto_urls: fotoUrls,
        foto_modificate_urls: [],
        prodotto_id: product.id,
        note: '',
      });
      toast.success('Bozza salvata!');
      onClose();
    } catch { toast.error('Errore salvataggio bozza'); }
    finally { setSaving(false); }
  };

  const handlePublishEbay = async () => {
    if (!ebayStatus?.collegato) { toast.error('Collega prima eBay dal profilo'); return; }
    setPublishing(true);
    try {
      const bozze = await api.get('/annunci/bozze');
      const ebayBozza = bozze.data.find(b => b.product_id === product.id && b.piattaforma === 'ebay' && b.stato === 'bozza');
      if (!ebayBozza) { toast.error('Bozza eBay non trovata'); setPublishing(false); return; }
      await api.put(`/annunci/bozze/${ebayBozza.id}`, { titolo: edit.titolo, descrizione: edit.descrizione, prezzo: parseFloat(edit.prezzo) || 0 });
      const fd = new FormData();
      fd.append('bozza_id', ebayBozza.id);
      // Invia foto modificate se disponibili
      const fotoEditateValide = fotoModificate.filter(Boolean);
      if (fotoEditateValide.length > 0) {
        fd.append('foto_modificate', JSON.stringify(fotoEditateValide));
      }
      const res = await api.post('/annunci/pubblica-ebay', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
      if (res.data.success) {
        setShowEbaySuccess({
          titolo: edit.titolo,
          link: res.data.link || res.data.url || '',
          itemId: res.data.item_id || '',
        });
      } else toast.error(res.data.error || 'Errore pubblicazione');
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore pubblicazione eBay'); }
    finally { setPublishing(false); }
  };

  const handlePublishVinted = async () => {
    setPublishing(true);
    // Apri finestra SUBITO (user gesture) per evitare popup blocker
    const win = window.open('about:blank', '_blank');
    try {
      let fotoCompresse = [];
      // Helper: comprimi immagine a 800px max, qualità 0.75 (buona qualità)
      const comprimiFoto = (src) => new Promise(resolve => {
        const img = new Image();
        img.onload = () => {
          const c = document.createElement('canvas');
          const max = 800;
          let w = img.width, h = img.height;
          if (w > h) { h = Math.round(h * max / w); w = max; } else { w = Math.round(w * max / h); h = max; }
          c.width = w; c.height = h;
          c.getContext('2d').drawImage(img, 0, 0, w, h);
          resolve(c.toDataURL('image/jpeg', 0.75));
        };
        img.onerror = () => resolve(null);
        img.src = src;
      });

      // Usa foto modificate se disponibili
      const fotoFinali = fotoUrls.map((orig, i) => fotoModificate[i] || orig);
      for (const url of fotoFinali.slice(0, 20)) {
        try {
          if (url.startsWith('data:')) {
            // Foto editata — ricomprimi a 800px/0.75 per buona qualità
            const b64 = await comprimiFoto(url);
            if (b64) fotoCompresse.push(b64);
            continue;
          }
          const path = url.startsWith('/') ? url.replace('/uploads/', '/media/') : `/media/${url}`;
          const proxyUrl = `${API_BASE}/proxy-image?url=${encodeURIComponent(path)}`;
          const r = await fetch(proxyUrl);
          const blob = await r.blob();
          const objectUrl = URL.createObjectURL(blob);
          const b64 = await comprimiFoto(objectUrl);
          URL.revokeObjectURL(objectUrl);
          if (b64) fotoCompresse.push(b64);
        } catch (e) { console.warn('[Foto] Errore:', e); }
      }
      const vintedData = {
        titolo: edit.titolo || '',
        descrizione: (edit.descrizione || '').replace(/<[^>]*>/g, '').trim(),
        prezzo: parseFloat(edit.prezzo || 0),
        categoria: product.category || '',
        marca: product.brand || '',
        taglia: product.taglia || '',
        colore: product.colore || '',
        condizioni: product.condition || 'Buone',
        fotoBase64: fotoCompresse,
      };
      const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(vintedData))));
      const vintedUrl = `https://www.vinted.it/items/new#IE_DRAFT=${encoded}`;

      // Salva dati per conferma al ritorno
      setVintedPublishData({
        titolo: edit.titolo,
        descrizione: edit.descrizione,
        prezzo: edit.prezzo,
        fotoUrl: fotoUrls[0] || '',
        prodottoId: product?.id,
      });

      // Naviga la finestra già aperta
      if (win && !win.closed) {
        win.location.href = vintedUrl;
      } else {
        // Fallback se la finestra è stata chiusa
        window.open(vintedUrl, '_blank');
      }
      toast.success('Apro Vinted con i dati...');
      setWaitingVintedConfirm(true);
    } catch {
      if (win && !win.closed) win.close();
      toast.error('Errore preparazione dati Vinted');
    }
    finally { setPublishing(false); }
  };

  const maxTitolo = piattaforma === 'vinted' ? 60 : 80;
  const stepLabels = ['Piattaforma', 'Genera', 'Modifica', 'Pubblica'];

  // ─── Stili ──────────────────────────────────────────────
  const overlay = { position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(13,11,30,0.92)', backdropFilter: 'blur(8px)', padding: 16 };
  const modal = { background: T.card, border: `1px solid ${T.border}`, borderRadius: 24, width: '100%', maxWidth: 600, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 0 60px rgba(124,58,237,0.3)', fontFamily: T.fontBody };
  const header = { padding: '20px 24px 0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' };
  const closeBtn = { background: 'none', border: 'none', color: T.muted, fontSize: '1.4rem', cursor: 'pointer', padding: 4, lineHeight: 1 };
  const body = { padding: '16px 24px 24px' };
  const cardStyle = (selected) => ({
    flex: 1, padding: '24px 16px', borderRadius: 16, border: `2px solid ${selected ? T.violetDark : T.border}`,
    background: selected ? 'rgba(124,58,237,0.1)' : T.elem, cursor: 'pointer', textAlign: 'center',
    transition: '0.2s', minHeight: 120, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
  });
  const btnPrimary = { padding: '12px 24px', borderRadius: 12, border: 'none', background: T.gradPrimary, color: 'white', fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer', transition: '0.15s', width: '100%' };
  const btnSecondary = { padding: '12px 24px', borderRadius: 12, border: `1px solid ${T.border}`, background: T.elem, color: T.text, fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', transition: '0.15s', width: '100%' };
  const btnGreen = { ...btnPrimary, background: 'linear-gradient(135deg, #4ade80, #22c55e)' };
  const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: 10, border: `1px solid ${T.border}`, background: T.elem, color: T.text, fontFamily: T.fontBody, fontSize: '0.88rem', outline: 'none', boxSizing: 'border-box' };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={header}>
          <div>
            <h2 style={{ fontFamily: T.fontDisplay, fontSize: '1.15rem', fontWeight: 800, color: T.text, margin: 0 }}>
              🤖 Crea Annuncio AI
            </h2>
            <p style={{ fontSize: '0.8rem', color: T.muted, margin: '4px 0 0' }}>{product.name || product.nome}</p>
          </div>
          <button style={closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* Step indicator */}
        <div style={{ padding: '12px 24px 0', display: 'flex', gap: 4, alignItems: 'center' }}>
          {stepLabels.map((label, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, flex: i < 3 ? 1 : 'none' }}>
              <div style={{
                width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.7rem', fontWeight: 700, flexShrink: 0,
                background: step >= i ? T.violetDark : T.elem,
                color: step >= i ? 'white' : T.muted,
                border: `1px solid ${step >= i ? T.violetDark : T.border}`,
              }}>
                {i + 1}
              </div>
              <span style={{ fontSize: '0.68rem', color: step >= i ? T.violet : T.muted, fontWeight: step === i ? 700 : 400, whiteSpace: 'nowrap' }}>
                {label}
              </span>
              {i < 3 && <div style={{ flex: 1, height: 1, background: step > i ? T.violetDark : T.border, marginLeft: 4 }} />}
            </div>
          ))}
        </div>

        <div style={body}>
          {/* STEP 0 — Selezione piattaforma */}
          {step === 0 && (
            <div>
              <p style={{ fontSize: '0.88rem', color: T.text, marginBottom: 16, fontWeight: 600 }}>
                Scegli la piattaforma di destinazione:
              </p>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={cardStyle(false)} onClick={() => handleSelectPiattaforma('vinted')}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = T.violetDark; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.transform = ''; }}>
                  <span style={{ fontSize: '2.2rem' }}>👗</span>
                  <span style={{ fontFamily: T.fontDisplay, fontWeight: 700, color: T.text, fontSize: '1rem' }}>Vinted</span>
                  <span style={{ fontSize: '0.72rem', color: T.muted }}>Max 60 caratteri titolo</span>
                </div>
                <div style={cardStyle(false)} onClick={() => handleSelectPiattaforma('ebay')}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = T.violetDark; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.transform = ''; }}>
                  <span style={{ fontSize: '2.2rem' }}>📦</span>
                  <span style={{ fontFamily: T.fontDisplay, fontWeight: 700, color: T.text, fontSize: '1rem' }}>eBay</span>
                  <span style={{ fontSize: '0.72rem', color: T.muted }}>Max 80 caratteri titolo</span>
                </div>
              </div>
              {/* Foto preview */}
              {fotoUrls.length > 0 && (
                <div style={{ marginTop: 16, display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
                  {fotoUrls.slice(0, 5).map((url, i) => (
                    <img key={i} src={`${API_BASE}${url}`} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 10, border: `1px solid ${T.border}`, flexShrink: 0 }} />
                  ))}
                  {fotoUrls.length > 5 && <span style={{ fontSize: '0.75rem', color: T.muted, alignSelf: 'center' }}>+{fotoUrls.length - 5}</span>}
                </div>
              )}
            </div>
          )}

          {/* STEP 1 — Generazione in corso */}
          {step === 1 && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ width: 48, height: 48, margin: '0 auto 20px', border: `3px solid ${T.violetDark}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              <p style={{ fontSize: '0.95rem', fontWeight: 600, color: T.text, marginBottom: 8 }}>
                {AI_TEXTS[aiTextIdx]}
              </p>
              <p style={{ fontSize: '0.78rem', color: T.muted }}>
                Ottimizzazione per {piattaforma === 'vinted' ? 'Vinted' : 'eBay'}...
              </p>
              <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
            </div>
          )}

          {/* STEP 2 — Modifica */}
          {step === 2 && (
            <div>
              {/* Foto cliccabili */}
              {fotoUrls.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, alignItems: 'center' }}>
                    {fotoUrls.slice(0, 5).map((url, i) => {
                      const isEdited = fotoModificate[i];
                      return (
                        <div key={i} style={{ position: 'relative', flexShrink: 0, cursor: 'pointer' }}
                          onClick={() => { if (!editorMontato) setEditorMontato(true); setShowEditorFoto(!showEditorFoto); }}>
                          <img src={isEdited || `${API_BASE}${url}`} alt="" style={{
                            width: 64, height: 64, objectFit: 'cover', borderRadius: 10,
                            border: isEdited ? `2px solid ${T.green}` : `1px solid ${T.border}`,
                          }} />
                          {isEdited && (
                            <span style={{
                              position: 'absolute', top: -4, right: -4, background: T.green,
                              color: '#fff', fontSize: '0.5rem', padding: '1px 4px', borderRadius: 6, fontWeight: 700,
                            }}>✓</span>
                          )}
                        </div>
                      );
                    })}
                    <button onClick={() => { if (!editorMontato) setEditorMontato(true); setShowEditorFoto(!showEditorFoto); }} style={{
                      padding: '6px 12px', borderRadius: 10, border: `1px solid ${T.violetDark}`,
                      background: showEditorFoto ? 'rgba(124,58,237,0.2)' : 'rgba(124,58,237,0.08)',
                      color: T.violet, fontSize: '0.75rem', fontWeight: 600, fontFamily: T.fontBody,
                      cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                    }}>
                      📷 {showEditorFoto ? 'Chiudi editor' : 'Modifica foto'}
                    </button>
                  </div>

                  {/* Editor foto inline — nascosto con CSS per mantenere stato */}
                  {editorMontato && <div style={{ marginTop: 8, display: showEditorFoto ? 'block' : 'none' }}>
                    <PhotoEditor
                      ref={editorRef}
                      fotoUrls={fotoUrls}
                      onFotoModificate={(edited) => {
                        setFotoModificate(edited);
                        const count = edited.filter(Boolean).length;
                        setFotoSalvateCount(count);
                      }}
                      apiBase={API_BASE}
                      defaultAperto={true}
                    />
                    {/* Bottone salva foto prominente */}
                    <button
                      onClick={async () => {
                        if (!editorRef.current) return;
                        setSalvandoFoto(true);
                        try {
                          await editorRef.current.applicaTutte();
                          toast.success(`Foto modificate salvate!`);
                        } catch { toast.error('Errore salvataggio foto'); }
                        finally { setSalvandoFoto(false); }
                      }}
                      disabled={salvandoFoto}
                      style={{
                        width: '100%', padding: '12px 0', borderRadius: 12, border: 'none',
                        background: 'linear-gradient(135deg, #4ade80, #22c55e)',
                        color: '#fff', fontSize: '0.9rem', fontWeight: 700, fontFamily: T.fontBody,
                        cursor: salvandoFoto ? 'wait' : 'pointer', marginTop: 10,
                        opacity: salvandoFoto ? 0.6 : 1,
                        boxShadow: '0 4px 16px rgba(74,222,128,0.3)',
                      }}
                    >
                      {salvandoFoto ? '⏳ Salvataggio in corso...' : '💾 Salva foto modificate'}
                    </button>
                  </div>}
                  {fotoSalvateCount > 0 && (
                    <div style={{
                      marginTop: 8, padding: '8px 14px', borderRadius: 10,
                      background: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.3)',
                      display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                      <span style={{ color: T.green, fontSize: '0.8rem', fontWeight: 600 }}>
                        ✓ {fotoSalvateCount}/{fotoUrls.length} foto salvate — verranno usate per la pubblicazione
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Titolo */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: T.muted }}>Titolo</label>
                  <span style={{ fontSize: '0.7rem', color: edit.titolo.length > maxTitolo ? T.red : T.muted }}>
                    {edit.titolo.length}/{maxTitolo}
                  </span>
                </div>
                <input type="text" value={edit.titolo} onChange={e => setEdit({ ...edit, titolo: e.target.value })}
                  maxLength={maxTitolo + 10} style={inputStyle} />
              </div>

              {/* Descrizione */}
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: T.muted, display: 'block', marginBottom: 4 }}>Descrizione</label>
                <textarea value={edit.descrizione} onChange={e => setEdit({ ...edit, descrizione: e.target.value })}
                  rows={isMobile ? 4 : 6} style={{ ...inputStyle, resize: 'vertical' }} />
              </div>

              {/* Tags (solo Vinted) */}
              {piattaforma === 'vinted' && (
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: T.muted, display: 'block', marginBottom: 4 }}>Hashtag / Tag</label>
                  <input type="text" value={edit.tags} onChange={e => setEdit({ ...edit, tags: e.target.value })}
                    placeholder="tag1, tag2, tag3" style={inputStyle} />
                </div>
              )}

              {/* Prezzo */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: T.muted, display: 'block', marginBottom: 4 }}>Prezzo (€)</label>
                <input type="number" value={edit.prezzo} onChange={e => setEdit({ ...edit, prezzo: e.target.value })}
                  min={0} step={0.01} style={{ ...inputStyle, maxWidth: 160 }} />
              </div>

              {/* Bottoni */}
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={handleRigenera} disabled={generating} style={{ ...btnSecondary, flex: 1, opacity: generating ? 0.5 : 1 }}>
                  🔄 Rigenera
                </button>
                <button onClick={() => setStep(3)} style={{ ...btnPrimary, flex: 2 }}>
                  ✅ Usa questo annuncio
                </button>
              </div>
            </div>
          )}

          {/* STEP 3 — Pubblica */}
          {step === 3 && (
            <div>
              {/* Riepilogo */}
              <div style={{ background: T.elem, borderRadius: 14, padding: 16, marginBottom: 20, border: `1px solid ${T.border}` }}>
                <h4 style={{ fontFamily: T.fontDisplay, fontSize: '0.85rem', fontWeight: 700, color: T.violet, marginBottom: 10 }}>
                  Riepilogo annuncio
                </h4>
                <div style={{ fontSize: '0.82rem', color: T.text, marginBottom: 6 }}>
                  <strong>Titolo:</strong> {edit.titolo}
                </div>
                <div style={{ fontSize: '0.82rem', color: T.muted, marginBottom: 6, maxHeight: 80, overflow: 'auto' }}>
                  <strong style={{ color: T.text }}>Descrizione:</strong> {edit.descrizione}
                </div>
                <div style={{ fontSize: '0.82rem', color: T.green, fontWeight: 700 }}>
                  💰 €{parseFloat(edit.prezzo || 0).toFixed(2)}
                </div>
                {piattaforma === 'vinted' && edit.tags && (
                  <div style={{ fontSize: '0.75rem', color: T.muted, marginTop: 6 }}>
                    🏷️ {edit.tags}
                  </div>
                )}
                {fotoSalvateCount > 0 && (
                  <div style={{ fontSize: '0.75rem', color: T.green, fontWeight: 600, marginTop: 6 }}>
                    📷 {fotoSalvateCount} foto modificate pronte
                  </div>
                )}
              </div>

              {/* Bottoni pubblicazione */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button onClick={handleSaveBozza} disabled={saving} style={{ ...btnSecondary, opacity: saving ? 0.5 : 1 }}>
                  {saving ? '⏳ Salvataggio...' : '📦 Salva come Bozza'}
                </button>

                {piattaforma === 'ebay' && (
                  <button onClick={handlePublishEbay} disabled={publishing} style={{ ...btnPrimary, opacity: publishing ? 0.5 : 1 }}>
                    {publishing ? '⏳ Pubblicazione...' : '🚀 Pubblica su eBay'}
                  </button>
                )}

                {piattaforma === 'vinted' && (
                  <button onClick={handlePublishVinted} disabled={publishing} style={{ ...btnGreen, opacity: publishing ? 0.5 : 1 }}>
                    {publishing ? '⏳ Preparazione...' : '👗 Pubblica su Vinted'}
                  </button>
                )}

                <button onClick={() => setStep(2)} style={{ background: 'none', border: 'none', color: T.muted, fontSize: '0.78rem', cursor: 'pointer', padding: 8 }}>
                  ← Torna a modifica
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ─── Popup conferma Vinted ─── */}
        {waitingVintedConfirm && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(13,11,30,0.92)', borderRadius: 24,
          }}>
            <div style={{
              background: T.card, border: `2px solid ${T.violetDark}`, borderRadius: 20,
              padding: '32px 28px', textAlign: 'center', maxWidth: 380, width: '100%',
              boxShadow: '0 0 60px rgba(124,58,237,0.4)',
            }}>
              <div style={{ fontSize: '3rem', marginBottom: 12 }}>🛍️</div>
              <h3 style={{ fontFamily: T.fontDisplay, fontSize: '1.2rem', fontWeight: 800, color: T.text, marginBottom: 8 }}>
                Hai pubblicato su Vinted?
              </h3>
              <p style={{ color: T.muted, fontSize: '0.82rem', marginBottom: 6 }}>
                Conferma per tracciare l'annuncio
              </p>
              {vintedPublishData?.titolo && (
                <p style={{ color: T.violet, fontSize: '0.85rem', fontWeight: 600, marginBottom: 20, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {vintedPublishData.titolo}
                </p>
              )}
              <button
                onClick={async () => {
                  setConfirmingVinted(true);
                  try {
                    const fotoUrl = vintedPublishData?.fotoUrl || '';
                    const fullFotoUrl = fotoUrl.startsWith('http') ? fotoUrl : (fotoUrl ? `${API_BASE}${fotoUrl}` : '');
                    await api.post('/annunci/vinted/log', {
                      titolo: vintedPublishData?.titolo || '',
                      prezzo: parseFloat(vintedPublishData?.prezzo || 0),
                      foto_url: fullFotoUrl,
                      descrizione: vintedPublishData?.descrizione || '',
                      url_annuncio: '',
                      prodotto_id: vintedPublishData?.prodottoId || null,
                    });
                    toast.success('Annuncio Vinted tracciato!');
                    setWaitingVintedConfirm(false);
                    setVintedPublishData(null);
                    onClose();
                  } catch { toast.error('Errore nel tracciamento'); }
                  finally { setConfirmingVinted(false); }
                }}
                disabled={confirmingVinted}
                style={{ ...btnPrimary, marginBottom: 8, opacity: confirmingVinted ? 0.6 : 1 }}
              >
                {confirmingVinted ? '⏳ Conferma...' : '✓ Si, ho pubblicato!'}
              </button>
              <button
                onClick={() => { setWaitingVintedConfirm(false); setVintedPublishData(null); }}
                style={{ ...btnSecondary, background: 'transparent' }}
              >
                No, non ancora
              </button>
            </div>
          </div>
        )}

        {/* ─── Popup successo eBay ─── */}
        {showEbaySuccess && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(13,11,30,0.92)', borderRadius: 24,
          }}>
            <div style={{
              background: T.card, border: `2px solid ${T.green}`, borderRadius: 20,
              padding: '32px 28px', textAlign: 'center', maxWidth: 380, width: '100%',
              boxShadow: '0 0 60px rgba(74,222,128,0.3)',
            }}>
              <div style={{ fontSize: '3rem', marginBottom: 12 }}>🎉</div>
              <h3 style={{ fontFamily: T.fontDisplay, fontSize: '1.2rem', fontWeight: 800, color: T.text, marginBottom: 8 }}>
                Pubblicato su eBay!
              </h3>
              <p style={{ color: T.violet, fontSize: '0.85rem', fontWeight: 600, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {showEbaySuccess.titolo}
              </p>
              <p style={{ color: T.green, fontSize: '0.82rem', marginBottom: 20 }}>
                Il tuo annuncio e ora visibile su eBay
              </p>
              {showEbaySuccess.link && (
                <a href={showEbaySuccess.link} target="_blank" rel="noopener noreferrer"
                  style={{ ...btnPrimary, display: 'block', textDecoration: 'none', textAlign: 'center', marginBottom: 8 }}>
                  Vedi su eBay
                </a>
              )}
              <button onClick={() => { setShowEbaySuccess(null); onClose(); }}
                style={{ ...btnSecondary, background: 'transparent' }}>
                Vai ai tuoi Annunci
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
